import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FlaskConical, TriangleAlert as AlertTriangle } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { PageHead } from "@/pages/admin/AdminApp";
import { Tone } from "@/components/store/StatusBadge";

export const Stat = ({ label, value, to, tone, testid }) => {
  const C = to ? Link : "div";
  return (
    <C to={to} data-testid={testid} className="card p-5 space-y-2 hover:border-[#2563EB] transition-colors block">
      <p className="text-xs muted font-medium">{label}</p>
      <p className={`font-display text-2xl font-bold ${tone || ""}`}>{value}</p>
    </C>
  );
};

export default function Dashboard() {
  const { t, money } = useI18n();
  const { can } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return null;
  const b = d.balances;
  return (
    <div data-testid="admin-dashboard">
      <PageHead title={t("admin.nav_dashboard")} sub={t("admin.dash_sub")} />
      {d.payment_mode === "test" && <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 mb-6 flex gap-3 text-sm text-amber-900"><FlaskConical className="w-5 h-5 shrink-0" />{t("admin.test_mode_banner")}</div>}
      {(d.orders_review > 0 || d.alerts_unread > 0) && <div className="grid sm:grid-cols-2 gap-3 mb-6">
        {d.orders_review > 0 && <Link to="/admin/orders?status=requires_review" className="card p-4 flex gap-3 items-center border-[#DC2626]/30 bg-[#DC2626]/5"><AlertTriangle className="w-5 h-5 text-[#DC2626]" /><span className="text-sm font-medium">{t("admin.review_needed", { n: d.orders_review })}</span></Link>}
        {d.alerts_unread > 0 && <Link to="/admin/alerts" className="card p-4 flex gap-3 items-center"><AlertTriangle className="w-5 h-5 text-amber-600" /><span className="text-sm font-medium">{t("admin.alerts_unread", { n: d.alerts_unread })}</span></Link>}
      </div>}
      <h2 className="text-sm font-semibold muted mb-3">{t("admin.operations")}</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <Stat testid="stat-orders-ready" label={t("order_status.ready_for_fulfillment")} value={d.orders_ready} to="/admin/orders?status=ready_for_fulfillment" />
        <Stat label={t("order_status.requires_review")} value={d.orders_review} to="/admin/orders?status=requires_review" tone={d.orders_review ? "text-[#DC2626]" : ""} />
        <Stat label={t("order_status.shipped")} value={d.orders_shipped} to="/admin/orders?status=shipped" />
        <Stat label={t("admin.returns_open")} value={d.returns_open} to="/admin/returns" />
      </div>
      {can("wallet.view") && <>
        <h2 className="text-sm font-semibold muted mb-3 flex items-center gap-2">{t("admin.finance_overview")} {d.balances_mode === "test" && <Tone tone="yellow">{t("admin.test_data")}</Tone>}</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          <Stat testid="stat-pending" label={t("wallet.pending")} value={money(b.pending)} to="/admin/finance?w=pending" tone="text-amber-600" />
          <Stat testid="stat-safe" label={t("wallet.safe")} value={money(b.safe)} to="/admin/finance?w=safe" tone="text-[#16A34A]" />
          <Stat label={t("wallet.held")} value={money(b.held)} to="/admin/finance?w=held" />
          <Stat label={t("wallet.liabilities")} value={money(b.liabilities)} to="/admin/finance?w=liabilities" tone={b.liabilities ? "text-[#DC2626]" : ""} />
        </div></>}
      <h2 className="text-sm font-semibold muted mb-3">{t("admin.catalog_integrations")}</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label={t("admin.products_active")} value={d.products_active} to="/admin/products" />
        <Stat label={t("admin.products_draft")} value={d.products_draft} to="/admin/products?status=draft" />
        <Stat label="AliExpress" value={t(d.integrations.aliexpress ? "admin.connected" : "admin.not_connected")} to="/admin/settings" tone={d.integrations.aliexpress ? "text-[#16A34A]" : "text-[#6B7280] text-base"} />
        <Stat label={t("admin.fulfillment_mode")} value={t(`settings.opt_${d.fulfillment_mode}`)} to="/admin/settings" tone="text-base" />
      </div>
    </div>
  );
}
